<script lang="ts">
	import type { TooltipOptions } from '$lib/actions/tooltip/types';
	import type { IconName } from '$lib/components/icons/types';
	import type { LocationButtonState } from '$lib/components/map/mapTools/types';
	import type { GeolocateControl } from 'maplibre-gl';
	import { tooltip } from '$lib/actions/tooltip/tooltip';
	import Button from '$lib/components/atoms/button/Button.svelte';
	import { addNotification } from '$lib/components/notifications/notifications';
	import * as m from '$paraglide/messages';
	import maplibregl from 'maplibre-gl';
	import { onMount } from 'svelte';
	import { getMapContext } from 'svelte-maplibre';

	interface Props {
		onGetLocation?: () => void;
	}

	const { onGetLocation }: Props = $props();

	let elementState: LocationButtonState = $state('unlocked');

	const icons: Record<LocationButtonState, IconName> = {
		unlocked: 'location-searching',
		locked: 'my-location',
		disabled: 'location-disabled',
	};

	const disabledTooltip: TooltipOptions = {
		header: m.location_control__tooltip__header(),
		text: m.location_control__tooltip__text(),
		placement: 'left-start',
	};

	const { map } = $derived(getMapContext());

	let disabled = $state(false);

	let control: GeolocateControl | null = $state(null);

	function lock() {
		elementState = 'locked';
	}

	function unlock() {
		elementState = 'unlocked';
	}

	function handleError() {
		elementState = 'disabled';
		disabled = true;
		addNotification(m.location_control__error());
	}

	onMount(() => {
		if (map && !control) {
			control = new maplibregl.GeolocateControl({
				positionOptions: { enableHighAccuracy: true },
				fitBoundsOptions: { maxZoom: 15 },
				trackUserLocation: true,
				showUserLocation: true,
				showAccuracyCircle: true,
			});
			map?.addControl(control, 'bottom-right');
			control.on('trackuserlocationstart', lock);
			control.on('trackuserlocationend', unlock);
			control.on('error', handleError);
			// remove native control button, we display our own
			control._container.parentNode?.removeChild(control._container);
		}
		return () => {
			if (map?.loaded() && control) {
				control.off('trackuserlocationstart', lock);
				control.off('trackuserlocationend', unlock);
				control.off('error', handleError);
				map?.removeControl(control);
				control = null;
			}
		};
	});
</script>

<div class="inline-block" use:tooltip={disabled ? disabledTooltip : undefined}>
	<Button
		ariaLabel={m.map_controls__aria_labels__location()}
		variant="filled"
		color="white"
		size="sm"
		icon={icons[elementState]}
		onclick={() => {
			const hasGeoloc = control?.trigger();
			if (hasGeoloc) {
				onGetLocation?.();
			}
		}}
		{disabled}
	/>
</div>
